import React, { useState, useEffect } from "react";
import { locationAPI, tenantAPI } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { formatDate } from "../../lib/utils";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { MapPin, Plus, Loader2, Pencil, Trash2 } from "lucide-react";

const emptyForm = { name: "", latitude: 0, longitude: 0, radius_meters: 100, is_active: true };

const GeoLocationsPage = () => {
  const { isSuperAdmin } = useAuth();
  const [locations, setLocations] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState("");
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const tenantParam = isSuperAdmin ? selectedTenant : undefined;
  const ready = !isSuperAdmin || !!selectedTenant;

  useEffect(() => {
    if (isSuperAdmin) {
      tenantAPI.getAll().then((res) => {
        setTenants(res.data);
        if (res.data.length > 0) setSelectedTenant(res.data[0].tenant_id);
      }).catch(() => {});
    } else {
      fetchLocations();
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin && selectedTenant) fetchLocations();
  }, [selectedTenant]);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const params = tenantParam ? { tenant_id: tenantParam } : {};
      const response = await locationAPI.getAll(params);
      setLocations(response.data);
    } catch (error) {
      toast.error("Failed to fetch locations");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowDialog(true);
  };

  const openEdit = (loc) => {
    setEditingId(loc.id);
    setFormData({
      name: loc.name,
      latitude: loc.latitude,
      longitude: loc.longitude,
      radius_meters: loc.radius_meters,
      is_active: loc.is_active,
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...formData };
      if (isSuperAdmin && selectedTenant) payload.tenant_id = selectedTenant;
      if (editingId) {
        await locationAPI.update(editingId, payload);
        toast.success("Location updated successfully");
      } else {
        await locationAPI.create(payload);
        toast.success("Location created successfully");
      }
      setShowDialog(false);
      setFormData(emptyForm);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save location");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (loc) => {
    if (!confirm(`Delete location "${loc.name}"? Employees mapped to it will be unassigned.`)) return;
    try {
      await locationAPI.delete(loc.id);
      toast.success("Location deleted");
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete location");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="geo-locations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Office Locations</h1>
          <p className="text-slate-500">Manage geo-fenced office locations used for attendance</p>
        </div>
        <div className="flex items-center gap-3">
          {isSuperAdmin && (
            <div className="w-56">
              <Select value={selectedTenant} onValueChange={setSelectedTenant}>
                <SelectTrigger data-testid="location-tenant-select">
                  <SelectValue placeholder="Select tenant" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.company_name} ({t.tenant_id})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button className="btn-primary" onClick={openCreate} disabled={!ready} data-testid="create-location-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Location
          </Button>
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">{editingId ? "Edit Office Location" : "Add Office Location"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="form-group">
              <Label>Location Name</Label>
              <Input
                placeholder="Main Office"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="location-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label>Latitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) || 0 })}
                  required
                  data-testid="location-latitude-input"
                />
              </div>
              <div className="form-group">
                <Label>Longitude</Label>
                <Input
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) || 0 })}
                  required
                  data-testid="location-longitude-input"
                />
              </div>
            </div>
            <div className="form-group">
              <Label>Geo-fence Radius (meters)</Label>
              <Input
                type="number"
                min="20"
                value={formData.radius_meters}
                onChange={(e) => setFormData({ ...formData, radius_meters: parseInt(e.target.value) || 100 })}
                required
                data-testid="location-radius-input"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-sm">Active Location</p>
                <p className="text-xs text-slate-500">Enable this location for attendance</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="location-active-switch"
              />
            </div>
            <Button type="submit" className="w-full btn-primary" disabled={saving} data-testid="submit-location">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? "Save Changes" : "Add Location"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Locations Table */}
      <Card>
        <CardContent className="p-0">
          {!ready ? (
            <div className="p-8 text-center text-slate-500">Select a tenant to view its locations.</div>
          ) : loading ? (
            <div className="flex items-center justify-center h-48"><div className="spinner w-8 h-8" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Location</TableHead>
                  <TableHead>Coordinates</TableHead>
                  <TableHead>Radius</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      No locations configured
                    </TableCell>
                  </TableRow>
                ) : (
                  locations.map((location) => (
                    <TableRow key={location.id} data-testid={`location-row-${location.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <MapPin className="w-5 h-5 text-blue-600" />
                          </div>
                          <span className="font-medium">{location.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {location.latitude?.toFixed(6)}, {location.longitude?.toFixed(6)}
                      </TableCell>
                      <TableCell>{location.radius_meters}m</TableCell>
                      <TableCell>
                        <Badge variant={location.is_active ? "default" : "secondary"}>
                          {location.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(location.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(location)} data-testid={`edit-location-${location.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(location)} data-testid={`delete-location-${location.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GeoLocationsPage;
